import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Trophy, Users } from "lucide-react";
import { fetchCategories } from "../../api/categoryService";
import { fetchUpcomingBattles } from "../../api/battleService";
import { useGameCtx } from "../../context/GameContext";
import { useAuth } from "../../context/AuthContext";
import IdentityChoiceModal from "../../components/modals/IdentityChoiceModal";
import ReadyToPlayModal from "../../components/modals/ReadyToPlayModal";

import "../../assets/css/Home.css";
import heroImage from "../../assets/images/entrance-image.jpg";

const FALLBACK_CATEGORIES = [
  { id: "music", slug: "musique", name: "Musique" },
  { id: "series", slug: "series-tv", name: "Series" },
  { id: "cinema", slug: "cinema", name: "Cinema" },
  { id: "logos", slug: "logos", name: "Logos" },
  { id: "anime", slug: "anime", name: "Anime" },
  { id: "sport", slug: "sport", name: "Sport" },
];

const HOW_TO_PLAY_CARDS = [
  { id: "step-1", image: "/images/choisir-categorie.jpg", label: "Choisir une catégorie" },
  { id: "step-2", image: "/images/jouer.jpg", label: "Devinez l'image" },
  { id: "step-3", image: "/images/gagner.jpg", label: "Gagnez des points" },
];

function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [homeBattles, setHomeBattles] = useState([]);

  const { setSelectedCategory } = useGameCtx();
  const { user } = useAuth();

  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [isReadyToPlayModalOpen, setIsReadyToPlayModalOpen] = useState(false);

  useEffect(() => {
    const pendingCat = localStorage.getItem("pendingCategory");
    if (pendingCat && user) {
      const parsedCat = JSON.parse(pendingCat);
      setSelectedCategory(parsedCat);
      setIsReadyToPlayModalOpen(true);
      localStorage.removeItem("pendingCategory");
    }
  }, [user, setSelectedCategory]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(Array.isArray(data) && data.length > 0 ? data : FALLBACK_CATEGORIES);
      } catch (error) {
        console.error("Erreur de chargement des categories :", error);
        setCategories(FALLBACK_CATEGORIES);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadBattles = async () => {
      try {
        const data = await fetchUpcomingBattles(controller.signal);
        setHomeBattles(Array.isArray(data?.battles) ? data.battles : []);
      } catch (error) {
        console.error("Erreur de chargement battles :", error);
        setHomeBattles([]);
      }
    };

    loadBattles();
    return () => controller.abort();
  }, []);

  const featuredCategories = useMemo(
    () => (categories.length > 0 ? categories.slice(0, 6) : FALLBACK_CATEGORIES),
    [categories]
  );

  const handleCategoryClick = async (category) => {
    setSelectedCategory({ slug: category.slug, name: category.name });

    if (user) {
      setIsReadyToPlayModalOpen(true);
      return;
    }

    setIsIdentityModalOpen(true);
    localStorage.setItem("pendingCategory", JSON.stringify(category));
  };

  const openQuickPlay = () => {
    window.dispatchEvent(new Event("openQuickPlay"));
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(95,181,111,0.18),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(47,157,226,0.16),_transparent_30%),linear-gradient(180deg,_#fffdf5_0%,_#effcf6_45%,_#e9f7ff_100%)] text-pixy-ink">
      <section className="mx-auto flex max-w-screen-2xl flex-col gap-10 px-4 pb-12 pt-8 sm:px-6 lg:flex-row lg:items-center lg:gap-16 lg:px-8 lg:pb-20 lg:pt-12">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 rounded-full border border-pixy-green/25 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-pixy-green shadow-sm">
            Quiz multijoueur live
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl font-black tracking-tight text-pixy-ink sm:text-5xl lg:text-6xl">
            Teste tes connaissances en temps réel
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg">
            Prêt à relever le défi ? Si tu as l'âge d'un challenger, une culture pop en béton et que ton canapé est ton meilleur allié, tu es exactement là où tu dois être. Cinéma, séries, musique, célébrités... choisis ton terrain de jeu, affronte d'autres joueurs et décroche la première place !
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={openQuickPlay}
              className="rounded-full bg-gradient-to-r from-pixy-green via-pixy-blue to-sky-500 px-7 py-4 text-sm font-bold uppercase tracking-[0.22em] text-white shadow-[0_18px_40px_rgba(47,157,226,0.28)] transition hover:-translate-y-0.5"
            >
              Jouer maintenant
            </button>
            <a
              href="#how-it-works"
              className="rounded-full border border-pixy-blue/20 bg-white/75 px-7 py-4 text-sm font-bold uppercase tracking-[0.18em] text-pixy-blue shadow-sm transition hover:bg-pixy-sky"
            >
              Voir comment ca marche
            </a>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <div className="rounded-3xl border border-emerald-100 bg-white/75 p-4 shadow-sm backdrop-blur">
              <div className="text-3xl font-black text-pixy-blue">24/7</div>
              <div className="mt-1 text-sm text-slate-600">Parties rapides</div>
            </div>
            <div className="rounded-3xl border border-sky-100 bg-white/75 p-4 shadow-sm backdrop-blur">
              <div className="text-3xl font-black text-pixy-green">Live</div>
              <div className="mt-1 text-sm text-slate-600">Classements et scores</div>
            </div>
            <div className="rounded-3xl border border-amber-100 bg-white/75 p-4 shadow-sm backdrop-blur">
              <div className="text-3xl font-black text-amber-500">MVP</div>
              <div className="mt-1 text-sm text-slate-600">Avatars, badges, progression</div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="relative overflow-hidden rounded-[36px] border border-white/80 bg-white/70 p-4 shadow-[0_30px_80px_rgba(47,157,226,0.16)] backdrop-blur">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(47,157,226,0.18),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(95,181,111,0.18),_transparent_30%)]" />
            <div className="relative rounded-[28px] border border-white/80 bg-white/80 p-3">
              <img
                src={heroImage}
                alt="Apercu PixyQuiz"
                className="h-[280px] w-full rounded-[24px] object-cover sm:h-[360px]"
              />
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {featuredCategories.slice(0, 4).map((category, index) => (
                  <button
                    key={category.slug || category.id || index}
                    type="button"
                    onClick={() => handleCategoryClick(category)}
                    className="rounded-2xl border border-emerald-100 bg-white/80 px-4 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-pixy-green/40 hover:bg-pixy-mist"
                  >
                    <div className="text-xs uppercase tracking-[0.28em] text-pixy-green">
                      CATÉGORIE
                    </div>
                    <div className="mt-2 text-lg font-semibold text-pixy-ink">
                      {category.name}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      Rejoindre la catégorie en un clic.
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="mx-auto max-w-screen-2xl px-4 py-12 sm:px-6 lg:px-8"
      >
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-pixy-green">Guide</p>
          <h2 className="mt-2 text-3xl font-black text-pixy-ink">Comment ça marche sur PixyQuiz ?</h2>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {HOW_TO_PLAY_CARDS.map((card) => (
            <div
              key={card.id}
              className="rounded-[32px] border border-emerald-100"
            >
              <div className="flex justify-center p-4 pb-0">
                <img
                  src={card.image}
                  alt={card.label}
                  className="w-full lg:w-3/4 xl:w-1/2 rounded-2xl object-contain"
                />
              </div>
              <div className="p-5 text-center">
                <h3 className="text-lg font-bold text-pixy-ink">{card.label}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {homeBattles.length > 0 ? (
        <section className="mx-auto max-w-screen-2xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-pixy-blue">Battles</p>
            <h2 className="mt-2 text-3xl font-black text-pixy-ink">Rendez-vous communautaires</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {homeBattles.slice(0, 6).map((battle) => (
              <button
                key={battle.id}
                type="button"
                onClick={() => navigate(`/battles/${battle.id}`)}
                className="rounded-[32px] border border-emerald-100 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-pixy-blue/35 hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full bg-pixy-mist px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-pixy-blue">
                    {battle.category?.name}
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full border border-emerald-100 px-3 py-1 text-xs font-semibold text-slate-500">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Intl.DateTimeFormat("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(battle.starts_at))}
                  </span>
                </div>
                <h3 className="mt-4 text-xl font-black text-pixy-ink">{battle.title}</h3>
                <p className="mt-1 text-sm text-slate-500">organisée par {battle.creator?.username}</p>
                <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-pixy-mist px-3 py-1.5">
                    <Trophy className="h-3.5 w-3.5 text-pixy-blue" />
                    {battle.round_count} parties
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-pixy-mist px-3 py-1.5">
                    <Users className="h-3.5 w-3.5 text-pixy-blue" />
                    {battle.participants_count} participent
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <IdentityChoiceModal
        isOpen={isIdentityModalOpen}
        onClose={() => setIsIdentityModalOpen(false)}
        onContinue={() => {
          setIsIdentityModalOpen(false);
          setIsReadyToPlayModalOpen(true);
        }}
      />

      <ReadyToPlayModal
        isOpen={isReadyToPlayModalOpen}
        onClose={() => setIsReadyToPlayModalOpen(false)}
      />
    </div>
  );
}

export default Home;

